import React from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryAttribute, SellerProductBasicDetails } from './types';
import TitleText from '../../atoms/TitleText';
import InfoText from '../../atoms/InfoText';

type Props = {
  basicDetails?: SellerProductBasicDetails;
  categoryAttributes?: CategoryAttribute[];
};

export default function ProductVariantsTable({
  basicDetails,
  categoryAttributes,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  if (
    !basicDetails?.applyVariants ||
    !basicDetails.variants ||
    basicDetails.variants.length === 0
  ) {
    return null;
  }

  // Get dynamic headers from categoryAttributes or fallback to extracting them from variants
  let dynamicHeaders: { id: string; nameEn: string; nameAr: string }[] = [];

  if (categoryAttributes && categoryAttributes.length > 0) {
    const usedAttributeIds = new Set(
      basicDetails.variants.flatMap((v) =>
        v.attributes.map((a) => a.categoryAttributeId)
      )
    );
    dynamicHeaders = categoryAttributes
      .filter((attr) => usedAttributeIds.has(attr.id))
      .map((attr) => ({
        id: attr.id,
        nameEn: attr.nameEn,
        nameAr: attr.nameAr,
      }));
  } else {
    const dynamicHeadersMap = new Map<string, { id: string; nameEn: string; nameAr: string }>();
    basicDetails.variants.forEach((v) => {
      v.attributes.forEach((attr) => {
        if (!dynamicHeadersMap.has(attr.categoryAttributeId)) {
          dynamicHeadersMap.set(attr.categoryAttributeId, {
            id: attr.categoryAttributeId,
            nameEn: attr.categoryAttributeName || attr.nameEn || '',
            nameAr: attr.categoryAttributeName || attr.nameAr || '',
          });
        }
      });
    });
    dynamicHeaders = Array.from(dynamicHeadersMap.values());
  }

  return (
    <div
      className="flex flex-col gap-4 w-full"
      data-testid="seller-product-variants-table-section"
    >
      <TitleText
        text={t('components.productDetailsPage.productVariants')}
        size="lg"
        className="!font-bold !text-[#090909] dark:!text-white"
      />

      <div className="overflow-x-auto border border-gray-200 dark:border-gray-800 rounded-lg">
        <table
          className="w-full text-sm text-left border-collapse"
          data-testid="seller-product-variants-table"
        >
          <thead
            className="bg-white dark:bg-gray-900 text-[#090909] dark:text-white font-semibold border-b border-gray-200 dark:border-gray-700"
            data-testid="seller-product-variants-table-head"
          >
            <tr data-testid="seller-product-variants-table-head-row">
              {dynamicHeaders.map((header) => (
                <th
                  key={header.id}
                  className="px-4 py-4 font-semibold border-r border-gray-200 dark:border-gray-800 last:border-r-0"
                  data-testid={`seller-product-variants-table-th-${header.id}`}
                >
                  <InfoText
                    text={
                      (locale === 'ar' ? header.nameAr : header.nameEn) ||
                      header.nameEn ||
                      header.nameAr ||
                      ''
                    }
                    className="!font-semibold !text-sm !m-0 !text-[#090909] dark:!text-white whitespace-nowrap"
                  />
                </th>
              ))}
              <th
                className="px-4 py-3 w-[134px] border-r border-gray-200 dark:border-gray-800 last:border-r-0"
                data-testid="seller-product-variants-table-th-sku"
              >
                <div
                  className="flex flex-col whitespace-nowrap"
                  data-testid="seller-product-variants-table-sku-header-wrapper"
                >
                  <InfoText
                    text={t('components.productDetailsPage.sku')}
                    className="!font-semibold !text-sm !m-0 !text-[#090909] dark:!text-white"
                  />
                </div>
              </th>
              <th
                className="px-4 py-4 font-semibold border-r border-gray-200 dark:border-gray-800 last:border-r-0"
                data-testid="seller-product-variants-table-th-stock"
              >
                <InfoText
                  text={t('components.addProduct.form.stock')}
                  className="!font-semibold !text-sm !m-0 !text-[#090909] dark:!text-white whitespace-nowrap"
                />
              </th>
              <th
                className="px-4 py-4 font-semibold border-r border-gray-200 dark:border-gray-800 last:border-r-0"
                data-testid="seller-product-variants-table-th-price"
              >
                <InfoText
                  text={t('components.productDetailsPage.price')}
                  className="!font-semibold !text-sm !m-0 !text-[#090909] dark:!text-white whitespace-nowrap"
                />
              </th>
            </tr>
          </thead>
          <tbody
            className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300"
            data-testid="seller-product-variants-table-body"
          >
            {basicDetails.variants.map((variant) => (
              <tr
                key={variant.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                data-testid={`variant-row-${variant.id}`}
              >
                {dynamicHeaders.map((header) => {
                  const attr = variant.attributes.find(
                    (a) => a.categoryAttributeId === header.id
                  );
                  const attrName = attr?.categoryAttributeDetailName || '—';
                  return (
                    <td
                      key={header.id}
                      className="px-4 py-4 border-r border-gray-200 dark:border-gray-800 last:border-r-0"
                      data-testid={`variant-attr-${header.id}-${variant.id}`}
                    >
                      <InfoText
                        text={attrName}
                        className="!text-sm !m-0 !text-[#090909] dark:!text-white"
                      />
                    </td>
                  );
                })}
                <td
                  className="px-4 py-4 border-r border-gray-200 dark:border-gray-800 last:border-r-0"
                  data-testid={`variant-sku-${variant.id}`}
                >
                  <InfoText
                    text={variant.sku}
                    className="!text-sm !m-0 !text-[#090909] dark:!text-white"
                  />
                </td>
                <td
                  className="px-4 py-4 border-r border-gray-200 dark:border-gray-800 last:border-r-0"
                  data-testid={`variant-stock-${variant.id}`}
                >
                  <InfoText
                    text={String(variant.stock)}
                    className="!text-sm !m-0 !text-[#090909] dark:!text-white"
                  />
                </td>
                <td
                  className="px-4 py-4 border-r border-gray-200 dark:border-gray-800 last:border-r-0"
                  data-testid={`variant-priceConverted-${variant.id}`}
                >
                  <InfoText
                    text={variant.priceConverted !== undefined && variant.priceConverted !== null ? `${variant.priceConverted} ${basicDetails.currency?.code || 'SAR'}` : '—'}
                    className="!text-sm !m-0 !text-[#090909] dark:!text-white"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
